import { ContentCopyRounded, DrawRounded, OpenInNewRounded } from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useConfirm } from 'material-ui-confirm';
import useErrors from '../../../../hooks/useErrors';
import server from '../../../../services/server';

function signerStatusChip(status) {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'signed') {
        return { label: 'Assinado', color: 'success' };
    }
    if (normalized === 'link-opened') {
        return { label: 'Link aberto', color: 'info' };
    }
    return { label: 'Pendente', color: 'warning' };
}

function SignersList({ signers, onCopy }) {
    if (!Array.isArray(signers) || signers.length === 0) {
        return (
            <Alert severity="info">
                Nenhum signatário encontrado no documento. Gere o contrato ou sincronize a assinatura.
            </Alert>
        );
    }

    return (
        <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
                Signatários ({signers.length})
            </Typography>
            {signers.map((signer, index) => {
                const status = signerStatusChip(signer.status);
                const isSigned = String(signer.status || '').toLowerCase() === 'signed';
                const signUrl = signer.sign_url || signer.link || null;
                const key = signer.token || signer.email || `${signer.name}-${index}`;

                return (
                    <Card key={key} variant="outlined">
                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={1.5}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                                justifyContent="space-between"
                            >
                                <Box minWidth={0} flex={1}>
                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                        <Typography fontWeight={600} noWrap title={signer.name}>
                                            {signer.name || `Signatário ${index + 1}`}
                                        </Typography>
                                        <Chip size="small" label={status.label} color={status.color} variant="outlined" />
                                    </Stack>
                                    <Typography variant="body2" color="text.secondary" noWrap title={signer.email}>
                                        {signer.email || '—'}
                                    </Typography>
                                    {signer.signed_at ? (
                                        <Typography variant="caption" color="text.secondary">
                                            Assinado em{' '}
                                            {new Date(signer.signed_at).toLocaleString('pt-BR', {
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            })}
                                        </Typography>
                                    ) : null}
                                </Box>

                                <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
                                    {signUrl ? (
                                        <>
                                            <Tooltip title="Copiar link de assinatura">
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => onCopy(signUrl)}
                                                    aria-label={`Copiar link de ${signer.name || 'signatário'}`}
                                                >
                                                    <ContentCopyRounded fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                endIcon={<OpenInNewRounded />}
                                                href={signUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Abrir
                                            </Button>
                                        </>
                                    ) : (
                                        <Typography variant="caption" color="text.secondary">
                                            {isSigned ? 'Link indisponível' : 'Sem link'}
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}

export function Contract({ mutate, contract }) {
    const { setErrors, enqueueSnackbar } = useErrors();
    const confirm = useConfirm();
    const { isSubmitting, handleSubmit } = useFormik({
        initialValues: {
            paymentDay: '10',
        },
        validateOnChange: true,
        validateOnMount: true,

        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);
                await confirm({
                    title: 'Assinatura do contrato',
                    description: `Você está gerando o contrato para assinatura digital. Os dados serão enviados para ${contract.contractorEmail}`,
                    confirmationText: 'Confirmo',
                    contentProps: { dividers: true },
                });
                await server.put(`subscription/${contract.uuid}/contract`);
                await mutate();
                setSubmitting(false);
                enqueueSnackbar('Contrato gerado.');
            } catch (error) {
                console.log(error);
                setSubmitting(false);
                setErrors(error);
            }
        },
    });

    async function handleSyncSignature() {
        try {
            await confirm({
                title: 'Sincronização da assinatura',
                description: `Você está sincronizando a assinatura do contrato.`,
                confirmationText: 'Confirmo',
            });
            await server.get(`subscription/${contract.uuid}/signature`);
            await mutate();
            enqueueSnackbar('Assinatura sincronizada com sucesso.');
        } catch (error) {
            setErrors(error);
        }
    }

    async function handleRegenerateDocument() {
        try {
            await confirm({
                title: 'Regenerar documento no ZapSign',
                description:
                    'O documento atual será invalidado (recusado/excluído no ZapSign) e um novo será gerado com os dados atuais do contrato. Os links antigos deixarão de valer. Continuar?',
                confirmationText: 'Invalidar e regenerar',
                confirmationButtonProps: { color: 'warning' },
            });
            await server.put(`subscription/${contract.uuid}/contract`, {
                regenerate: true,
                rejectReason: 'Documento invalidado — novo contrato gerado pela Eurobem (admin).',
            });
            await mutate();
            enqueueSnackbar('Documento anterior invalidado e novo contrato enviado.');
        } catch (error) {
            if (error !== undefined) setErrors(error);
        }
    }

    const handleCopySignUrl = url => {
        navigator.clipboard.writeText(url);
        enqueueSnackbar('Link de assinatura copiado.', { variant: 'success' });
    };

    const hasContract = Boolean(contract.integrationId);
    const hasTerm = Boolean(contract);
    const hasSignature = Boolean(contract?.signedAt);
    const concludedTermAt = Boolean(contract?.contractData?.concludedTermAt);
    const uploads = contract?.Uploads || [];
    const originalFile = uploads.find(i => i.description === 'contrato_original')?.fileUrl;
    const signedUpload = uploads.find(i => i.description === 'contrato_assinado')?.fileUrl;
    const signedFile = Boolean(hasSignature) || contract?.status === 'SIGNED';
    const signers = contract?.contractData?.document?.signers || [];
    const signedCount = signers.filter(s => String(s.status || '').toLowerCase() === 'signed').length;
    const documentUrl = signedFile && signedUpload ? signedUpload : originalFile;

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Typography variant="h3" component="h2">
                    Assinatura do contrato
                </Typography>

                {hasTerm && !hasContract && (
                    <>
                        <Divider />
                        <Typography variant="body1">
                            Você preencheu a proposta com os dados solicitados. Agora vamos para a etapa onde você precisa
                            gerar o contrato para assinatura digital.
                        </Typography>
                        <Typography variant="body1">
                            Após a geração do contrato, ele deverá ser assinado por todos os signatários para formalizar a
                            adesão. O clube será efetivamente criado após a confirmação dos pagamentos.
                        </Typography>
                        <Card>
                            <CardContent>
                                <Stack direction="row" columnGap={2} alignItems="center" justifyContent="space-around">
                                    <DrawRounded fontSize="large" sx={{ fontSize: 70 }} color="action" />
                                    <Divider orientation="vertical" flexItem />
                                    <Typography variant="body1" textAlign="center">
                                        O contrato será enviado para{' '}
                                        <strong>{contract?.contractData?.contract?.contractorEmail}</strong>
                                    </Typography>
                                    <Divider orientation="vertical" flexItem />
                                    <Stack direction="row" sx={{ width: 200 }} justifyContent="center">
                                        <Button
                                            sx={{ alignSelf: 'flex-end' }}
                                            variant="outlined"
                                            disabled={isSubmitting || hasSignature || concludedTermAt}
                                            onClick={handleSubmit}
                                        >
                                            Gerar contrato
                                        </Button>
                                    </Stack>
                                </Stack>
                            </CardContent>
                        </Card>
                    </>
                )}

                {hasContract && !hasSignature && (
                    <>
                        <Divider />
                        <Typography variant="body1">
                            O contrato foi gerado e enviado aos signatários. Use os links abaixo para acompanhar ou
                            reenviar a assinatura. O sistema atualiza automaticamente quando todos assinarem.
                        </Typography>
                        <Alert severity="info">
                            Aguardando assinaturas
                            {signers.length > 0 ? `: ${signedCount} de ${signers.length} concluídas` : ''}.
                        </Alert>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                            <Button variant="outlined" color="warning" onClick={handleSyncSignature}>
                                Sincronizar assinatura
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleRegenerateDocument}
                                disabled={isSubmitting || Boolean(contract?.clubId)}
                            >
                                Regenerar no ZapSign
                            </Button>
                        </Stack>
                    </>
                )}

                {hasContract && hasSignature && (
                    <>
                        <Divider />
                        <Alert severity="success">Contrato assinado por todos os signatários.</Alert>
                    </>
                )}

                {hasContract && (
                    <>
                        <Divider />
                        <SignersList signers={signers} onCopy={handleCopySignUrl} />
                    </>
                )}

                {documentUrl && (
                    <Button
                        variant="outlined"
                        color={signedFile ? 'success' : 'secondary'}
                        href={documentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ alignSelf: 'flex-start' }}
                    >
                        {signedFile ? 'Ver contrato assinado' : 'Ver contrato'}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
}
